import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { ISubtitle } from './ISubtitle';
import { lastValueFrom } from './rxjs/lastValueFrom';
import { ISubtitleDisplayOptions } from './shared/SubtitleDisplayOptions';

@Injectable()
export class SubtitleService {
	private readonly rxTime: RegExp = /((\d+):)?(\d+):(\d+)([,.](\d+))?/;

	private readonly _subtitleOptions = signal<ISubtitleDisplayOptions>({} as any);
	public readonly subtitleOptions = this._subtitleOptions.asReadonly();

	constructor(
		private httpClient: HttpClient
	) {}

	public getParsedSubtitle(src: string): Promise<ISubtitle[]> {
		if (typeof src !== 'string') {
			return null;
		}

		return lastValueFrom(this.httpClient.get(src, { responseType: 'text' }))
			.then((response: any) => this.parseSubtitle(response));
	}

	public updateSubtitleOptions(subtitleOptions: ISubtitleDisplayOptions): void {
		this._subtitleOptions.set(subtitleOptions || {} as any);
	}

	private parseSubtitle(subtitleText: string): ISubtitle[] {
		const subtitles: ISubtitle[] = [];
		const segments: string[] = subtitleText.split(/\r?\n\s*\r?\n/); //split by double line breaks (may have white space in between)

		segments.forEach(segment => {
			//split the segment into lines
			const lines: string[] = segment.split(/\r?\n/);

			const lineIndex = lines.findIndex(line => line.includes('-->'));
			if (lines.length >= 2 && lineIndex >= 0) { //actually has content
				const times = lines[lineIndex].split(/\s*-->\s*/);
				const start = this.parseTime(times[0]);
				const end = this.parseTime(times[1]);
				const text = lines.slice(lineIndex+1).join('\n');

				// do not include this line if start/end is invalid
				if (start == undefined || end == undefined) {
					return;
				}

				subtitles.push({
					start,
					end,
					text
				});
			}
		});

		//sort them since segments in input may not be sequenced correctly
		subtitles.sort(this.subtitleSortCompare);

		return subtitles;
	}

	private parseTime(time: string): number {
		const arr: RegExpMatchArray = time.match(this.rxTime);

		if (!arr) {
			return;
		}

		return (parseInt(arr[2], 10) || 0) * 3600 +
			parseInt(arr[3], 10) * 60 +
			parseInt(arr[4], 10) +
			(parseInt(arr[6], 10) || 0) * 0.001;
	}

	private subtitleSortCompare(a: ISubtitle, b: ISubtitle): 1 | -1 | 0 {
		return a.start < b.start ? -1 : a.start > b.start ? 1 : 0;
	}
}
